import chatApi from 'api/chatApi';
import { useAppSelector } from 'app/hooks';
import { ChatMessageType, ChatMessageTypeRequest, GiveAwayResponseModel } from 'models/chatMessage';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { selectShowChatMobile } from 'redux/chatSlice';
import { selectCurrentUser } from 'redux/userSlice';
import SockJS from 'sockjs-client';
import { Message, over } from 'stompjs';
import { getLocalStorage } from 'utils/sessionStorage';

interface MessageProviderProps {
  children: ReactNode;
}

export interface MessageContextValue {
  globalMessages: ChatMessageType[];
  sendMessage: (message: ChatMessageTypeRequest) => boolean;
  getMessages: (callback: () => void) => void;
  canDelete: boolean;
  hasMore: boolean;
  loading: boolean;
  lastMessageId?: string;
  setLoading: (loading: boolean) => void;
  unreadChatNumber: number;
  resetUnread: () => void;
  dropGiveAways: GiveAwayResponseModel[];
  spinGiveAways: GiveAwayResponseModel[];
  removeGiveAways: (promotionCode: string) => void;
  removeSpinAway: () => void;
  handleSetActiveReply: (activeReply: string | null) => void;
  activeReplyId: string | null;
}

export const MessageContext = createContext<MessageContextValue | null>(null);
const PAGE_SIZE = 30;

const CHAT_ROOM_GATEWAY = `${process.env.REACT_APP_CHAT_ROOM_GATEWAY_URL}/ws`;

export const MessageProvider: React.FC<MessageProviderProps> = ({ children }) => {
  const [globalMessages, setGlobalMessages] = useState<ChatMessageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastMessageId, setLastMessageId] = useState<string>();
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [unreadChatNumber, setUnreadChatNumber] = useState<number>(0);
  const isShowChatMobile = useAppSelector(selectShowChatMobile);
  const [dropGiveAways, setDropGiveaway] = useState<GiveAwayResponseModel[]>([]);
  const [spinGiveAways, setSpinGiveaway] = useState<GiveAwayResponseModel[]>([]);
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
  const [pageagble, setPageagble] = useState({
    page: 0,
    size: PAGE_SIZE,
    sort: 'date,desc',
  });

  const user = useSelector(selectCurrentUser);

  const stompClientRef = useRef<any>(null);
  const isConnectedRef = useRef(false);
  const currentUserIdRef = useRef<number | undefined>(undefined);

  const createStompClient = useCallback(() => {
    const client = over(new SockJS(CHAT_ROOM_GATEWAY));
    client.debug = () => {};
    return client;
  }, []);

  const onError = (error: any) => {
    isConnectedRef.current = false;
  };

  const userJoin = useCallback(() => {
    if (!isConnectedRef.current || !stompClientRef.current) return;

    if (user?.id) {
      const newMessage = {
        message: '',
        type: 'JOIN',
        date: new Date(),
        token: getLocalStorage('access_token_terabet'),
      };

      try {
        stompClientRef.current.send('/terabet/message', {}, JSON.stringify(newMessage));
      } catch (error) {
        console.log('Error sending join message:', error);
      }
    } else {
      console.log('Guest connected (no user login)');
    }
  }, [user?.id]);

  const onConnected = useCallback(() => {
    isConnectedRef.current = true;
    currentUserIdRef.current = user?.id;

    try {
      stompClientRef.current?.subscribe('/chatroom/public', onMessageReceived);
      userJoin();
      console.log('Connect socket message');
    } catch (error) {
      console.log('Error in onConnected:', error);
    }
  }, [user?.id, userJoin]);

  const onMessageReceived = useCallback(
    (payload: Message) => {
      try {
        const payloadData: ChatMessageType = JSON.parse(payload.body);
        switch (payloadData.type) {
          case 'UPDATE_MESSAGE':
            setGlobalMessages((prev) =>
              prev.map((msg) => {
                if (payloadData.parentId) {
                  if (msg.messageId === payloadData.parentId) {
                    return {
                      ...msg,
                      replies: msg.replies.map((reply) =>
                        reply.messageId === payloadData.messageId
                          ? {
                              ...reply,
                              dislikeUsers: payloadData.dislikeUsers,
                              likeUsers: payloadData.likeUsers,
                            }
                          : reply,
                      ),
                    };
                  }
                } else {
                  if (msg.messageId === payloadData.messageId) {
                    return {
                      ...msg,
                      dislikeUsers: payloadData.dislikeUsers,
                      likeUsers: payloadData.likeUsers,
                    };
                  }
                }
                return msg;
              }),
            );
            break;
          case 'DELETE_MESSAGE':
            setGlobalMessages((prev) =>
              prev.filter((msg) => msg.messageId !== payloadData.messageId),
            );
            break;
          case 'SPIN_AND_DROP':
            const data: GiveAwayResponseModel = JSON.parse(payloadData.message);
            if (data.promotionType === 'DROP') {
              setDropGiveaway((prev) => [...prev, data]);
            }
            if (data.promotionType === 'SPIN') {
              setSpinGiveaway((prev) => [...prev, data]);
            }
            break;
          default:
            if (payloadData.type === 'MESSAGE') {
              if (
                user?.id &&
                payloadData.userId !== user.id &&
                !isShowChatMobile &&
                !payloadData.parentId
              ) {
                setUnreadChatNumber((prev) => prev + 1);
              }
              setGlobalMessages((prev) => {
                if (payloadData.parentId) {
                  return prev.map((msg) =>
                    msg.messageId === payloadData.parentId
                      ? { ...msg, replies: [...(msg.replies || []), payloadData] }
                      : msg,
                  );
                }
                return [...prev, payloadData];
              });
              return;
            }
            setGlobalMessages((prev) => [...prev, payloadData]);
            break;
        }
      } catch (error) {
        console.log('Error parsing message:', error);
      }
    },
    [isShowChatMobile, user?.id],
  );

  const disconnect = useCallback(() => {
    if (stompClientRef.current && isConnectedRef.current) {
      try {
        stompClientRef.current.disconnect(() => {
          isConnectedRef.current = false;
          currentUserIdRef.current = undefined;
          console.log('Disconnect socket message');
        });
      } catch (error) {
        isConnectedRef.current = false;
        currentUserIdRef.current = undefined;
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (isConnectedRef.current) {
      disconnect();
    }
    stompClientRef.current = createStompClient();
    try {
      stompClientRef.current.connect({}, onConnected, onError);
    } catch (error) {
      console.log('Error connecting:', error);
    }
  }, [createStompClient, onConnected, disconnect, user?.id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (
          !isConnectedRef.current ||
          !stompClientRef.current ||
          stompClientRef.current.connected === false
        ) {
          connect();
        }
      } else {
        console.log('Tab is now hidden');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, []);

  useEffect(() => {
    const userChanged = currentUserIdRef.current !== user?.id;

    if (userChanged) {
      connect();
    }
  }, [user?.id, connect]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      disconnect();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      disconnect();
    };
  }, [disconnect]);

  const getMessages = useCallback(
    async (callback?: () => void) => {
      if (!hasMore || loading) return;

      setLoading(true);
      try {
        const { ok, body } = await chatApi.getMessages(pageagble);
        if (ok && body) {
          setCanDelete(body.canDelete);
          if (body.messages.length === 0) {
            setHasMore(false);
          } else {
            setPageagble((prevState) => ({ ...prevState, page: prevState.page + 1 }));
            setGlobalMessages((prevState) => [...body.messages.reverse(), ...prevState]);
            if (callback) {
              callback();
            } else {
              setLastMessageId(body.messages[0].messageId);
            }
          }
        }
      } catch (error) {
        console.log('Error getting messages:', error);
      } finally {
        setLoading(false);
      }
    },
    [pageagble.page, hasMore, loading],
  );

  useEffect(() => {
    getMessages();
  }, []);

  const sendMessage = useCallback(
    (message: ChatMessageTypeRequest) => {
      if (user?.id && isConnectedRef.current && stompClientRef.current) {
        try {
          stompClientRef.current.send('/terabet/message', {}, JSON.stringify(message));
          return true;
        } catch (error) {
          console.log('Error sending message:', error);
          return false;
        }
      } else {
        console.log('Cannot send message: user not logged in or not connected');
        return false;
      }
    },
    [user?.id],
  );

  const resetUnread = () => {
    setUnreadChatNumber(0);
  };

  const removeGiveAways = (promotionCode: string) => {
    setDropGiveaway((prev) => [...prev.filter((x) => x.promotionCode !== promotionCode)]);
    setSpinGiveaway((prev) => [...prev.filter((x) => x.promotionCode !== promotionCode)]);
  };

  const removeSpinAway = () => {
    setSpinGiveaway([]);
  };

  const handleSetActiveReply = (messageId: string | null) => {
    setActiveReplyId(messageId);
  };

  const value: MessageContextValue = {
    globalMessages,
    sendMessage,
    getMessages,
    canDelete,
    hasMore,
    loading,
    lastMessageId,
    setLoading,
    unreadChatNumber,
    resetUnread,
    dropGiveAways,
    spinGiveAways,
    removeGiveAways,
    removeSpinAway,
    handleSetActiveReply,
    activeReplyId,
  };
  return <MessageContext.Provider value={value}>{children}</MessageContext.Provider>;
};

export const useChatConntext = (): MessageContextValue => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
